import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { getSingleReportCSV } from 'Services';
import { RQ_SINGLE_REPORT_CSV } from 'Utils';
import { useTranslation } from 'react-i18next';
import useReportCsvErrorHandler from './useReportCsvErrorHandler';

type UseSingleReportCSVProps = {
    reportId: number;
};

function useSingleReportCSV({ reportId }: UseSingleReportCSVProps) {
    const { t } = useTranslation();
    const errorHandler = useReportCsvErrorHandler({
        preContentMessage: t('Failed to export CSV'),
        title: t('Unable to export CSV')
    });

    const [shouldPoll, setShouldPoll] = useState(false);
    const [allowedToFetch, setAllowedToFetch] = useState(false);

    return useQuery([RQ_SINGLE_REPORT_CSV, reportId], () => getSingleReportCSV(reportId!), {
        enabled: allowedToFetch,
        onSuccess: (response) => {
            setAllowedToFetch(true);
            if (response.status === 200) {
                setAllowedToFetch(false);
                setShouldPoll(false);
                const [, filename] = response.headers['content-disposition'].split('filename=');
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.download = `${filename}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                setShouldPoll(true);
            }
        },
        onError: (error) => {
            setAllowedToFetch(false);
            setShouldPoll(false);
            errorHandler(error);
        },
        refetchInterval: shouldPoll ? 5000 : false,
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: false
    });
}

export default useSingleReportCSV;
